/**
 * Quiet client-side rollover for calendar refreshes. Only missed eligible activities are selected
 * by the server; the same browser solver and narrow proposal validation used by Solve do the work.
 * Concurrent day/week refreshes share one in-flight operation for the current owner and token.
 * There is no implicit server optimizer fallback when local computing is unavailable.
 * @module apps/web/src/lib/calendarRollover
 */
import { api } from './api';
import { previewBrowserSchedule } from './browserScheduling';
import { runBrowserScheduleFlow, getSchedulingSessionEpoch } from './browserScheduleFlow';
import { schedulingProposalFromPreview } from './schedulingProtocol';
import { captureTaskSnapshot, isTaskSnapshotCurrent, useTasksStore } from '@/stores/tasksStore';
import { useUserStore } from '@/stores/userStore';
import { taskToUI } from '@fluxability/types';

/** At most one quiet rollover is active per authenticated browser session. */
let pending:
  | {
      /** Session owner captured at start. */ owner: string;
      /** Parent-only token fence. */ token: string | null;
      /** Shared completion promise. */ operation: Promise<void>;
    }
  | undefined;

/** Compute only missed activities locally and publish records only after server acceptance. */
export function rolloverCalendarLocally(weekStart?: string): Promise<void> {
  const owner = useUserStore.getState().userId;
  if (!owner) return Promise.resolve();
  const token = localStorage.getItem('flexiplanner-session-token');
  if (pending?.owner === owner && pending.token === token) return pending.operation;
  const operation = runRollover(owner, token, weekStart);
  const current = { owner, token, operation };
  pending = current;
  void operation
    .finally(() => {
      if (pending === current) pending = undefined;
    })
    .catch(() => {});
  return operation;
}

/** Bind every compute/write boundary to the current session and discard stale local snapshots. */
async function runRollover(owner: string, token: string | null, weekStart?: string): Promise<void> {
  const abort = new AbortController();
  const sessionEpoch = getSchedulingSessionEpoch();
  let jobId: string | undefined;
  const assertOwner = () => {
    if (
      sessionEpoch !== getSchedulingSessionEpoch() ||
      owner !== useUserStore.getState().userId ||
      token !== localStorage.getItem('flexiplanner-session-token')
    )
      throw new Error('Your scheduling session changed.');
  };
  try {
    const response = await api.schedule.autoSchedule(weekStart, 'browser', abort.signal, true);
    jobId = response.data?.purpose === 'full' ? undefined : response.data?.job_id;
    assertOwner();
    if (!response.success || !response.data)
      throw new Error(response.error?.message || 'Could not prepare missed activities.');
    if (!response.data.browser || response.data.purpose === 'full') return;
    jobId = response.data.job_id;
    let snapshot = captureTaskSnapshot();
    await runBrowserScheduleFlow({
      initial: response.data,
      signal: abort.signal,
      assertCurrent: () => {
        assertOwner();
        if (!isTaskSnapshotCurrent(snapshot))
          throw new Error('Activities changed during rollover.');
      },
      solve: previewBrowserSchedule,
      proposal: schedulingProposalFromPreview,
      preview: () => {},
      clearPreview: () => {},
      submit: async (proposal, signal) => {
        const reply = await api.schedule.proposeSchedule(proposal, signal);
        if (!reply.success || !reply.data)
          throw new Error(reply.error?.message || 'Missed placements could not be saved.');
        return reply.data;
      },
      accepted: status => {
        assertOwner();
        if (isTaskSnapshotCurrent(snapshot) && status.changes?.length)
          useTasksStore.getState().upsertTasks(status.changes.map(taskToUI));
        snapshot = captureTaskSnapshot();
      },
    });
  } catch (error) {
    if (
      jobId &&
      owner === useUserStore.getState().userId &&
      token === localStorage.getItem('flexiplanner-session-token')
    )
      void api.schedule.cancelAutoSchedule(jobId);
    throw error;
  } finally {
    abort.abort();
  }
}
